/* ==========================================================================
 * Attendance Chat Classifier — BETA
 * ==========================================================================
 *  Non-destructive alternative to the classic Excel upload in Control Panel.
 *  Accepts one or many WhatsApp / chat exports (xlsx / csv) with columns
 *  DateTime, Sender, Message. Parses each day's "in by / in now / oof at /
 *  oof now" cues, classifies into 10:00-10:15 / 10:15-10:30 / after 10:30
 *  slabs (arrival) and 18:00-18:30 / 18:30-19:00 / before 18:00 slabs (exit).
 *  Slabs are clickable → modal shows the exact messages that produced the
 *  bucket, highlighting the "final" pick. WFH/OOF still filled in manually
 *  (inline editors) or via a small companion XLSX upload.
 *
 *  Ported faithfully from the user's HTML reference tool; SLABS_IN / OUT and
 *  the pickFinal + classify + slabIn/slabOut logic are 1:1 copies.
 * ========================================================================== */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Expandable } from "@/components/Expandable";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UploadCloud, X, FileSpreadsheet, Loader2, Send, ChevronRight, CheckCircle2, AlertTriangle, Pencil } from "lucide-react";

const SHIFTS = {
    corporate: {
        key: "corporate",
        label: "Corporate Shift (10:00 – 19:00)",
        // In-slabs — grace window from shift start
        inSlabs: ["Between 1000-1015", "Between 1015-1030", "After 1030"],
        outSlabs: ["Between 1800-1830", "Between 1830-1900", "Before 1800"],
        // Minutes since midnight, sorted so slabIn/slabOut become table lookups.
        inBounds: [
            { max: 600, label: "Before 1000" },
            { max: 615, label: "Between 1000-1015" },
            { max: 630, label: "Between 1015-1030" },
            { max: Infinity, label: "After 1030" },
        ],
        outBounds: [
            { max: 1080, label: "Before 1800" },     // < 18:00
            { max: 1110, label: "Between 1800-1830" },
            { max: 1140, label: "Between 1830-1900" },
            { max: Infinity, label: "After 1900" },
        ],
    },
    firmware: {
        key: "firmware",
        label: "Firmware Shift (09:00 – 18:00)",
        inSlabs: ["Between 0900-0915", "Between 0915-0930", "After 0930"],
        outSlabs: ["Between 1700-1730", "Between 1730-1800", "Before 1700"],
        inBounds: [
            { max: 540, label: "Before 0900" },
            { max: 555, label: "Between 0900-0915" },
            { max: 570, label: "Between 0915-0930" },
            { max: Infinity, label: "After 0930" },
        ],
        outBounds: [
            { max: 1020, label: "Before 1700" },     // < 17:00
            { max: 1050, label: "Between 1700-1730" },
            { max: 1080, label: "Between 1730-1800" },
            { max: Infinity, label: "After 1800" },
        ],
    },
};
const slabsForShift = (key) => SHIFTS[key] || SHIFTS.corporate;
const allSlabCols = (shiftKey) => {
    const s = slabsForShift(shiftKey);
    return [...s.inSlabs, ...s.outSlabs];
};

// ---- classifier logic (ported verbatim) ----
const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const fullYear = (y) => (y < 100 ? 2000 + y : y);
const clean = (v) => String(v ?? "").replace(/[\u200e\u200f\u202a-\u202f\u00a0]/g, " ").replace(/^\s*\[|\]\s*-?\s*$/g, "").trim();

const parseTime = (v) => {
    if (typeof v === "number") {
        const c = XLSX.SSF.parse_date_code(v);
        return c ? { h: c.H, mi: c.M } : null;
    }
    const m = clean(v).match(/(\d{1,2})[:.](\d{2})(?:[:.]\d{2})?\s*([ap])?\.?\s?(?:m\.?)?/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const mi = parseInt(m[2], 10);
    const ap = (m[3] || "").toLowerCase();
    if (ap === "p" && h < 12) h += 12;
    if (ap === "a" && h === 12) h = 0;
    return { h, mi };
};

// Date part: Excel serial, D/M/Y (WhatsApp default), Y-M-D, M/D/Y (auto-detected) or month names.
const parseDatePart = (v) => {
    if (typeof v === "number") {
        const c = XLSX.SSF.parse_date_code(v);
        if (!c || c.y < 1990) return null;
        return { y: c.y, mo: c.m, d: c.d, serial: true, time: v % 1 ? { h: c.H, mi: c.M } : null, rest: "" };
    }
    const s = clean(v);
    let m = s.match(/^(\d{1,4})[/\-.](\d{1,2})[/\-.](\d{1,4})/);
    if (m) {
        let [a, b, c] = [m[1], m[2], m[3]].map((x) => parseInt(x, 10));
        let y, mo, d;
        if (m[1].length === 4) [y, mo, d] = [a, b, c];
        else {
            [d, mo, y] = [a, b, fullYear(c)];
            if (mo > 12 && d <= 12) [d, mo] = [mo, d];
        }
        return { y, mo, d, rest: s.slice(m[0].length) };
    }
    m = s.match(/^(\d{1,2})(?:st|nd|rd|th)?[\s\-]+([A-Za-z]{3,9})[\s\-,]+(\d{2,4})/);
    if (m && MONTHS[m[2].toLowerCase().slice(0, 3)]) {
        return { y: fullYear(parseInt(m[3], 10)), mo: MONTHS[m[2].toLowerCase().slice(0, 3)], d: parseInt(m[1], 10), rest: s.slice(m[0].length) };
    }
    m = s.match(/^([A-Za-z]{3,9})\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{2,4})/);
    if (m && MONTHS[m[1].toLowerCase().slice(0, 3)]) {
        return { y: fullYear(parseInt(m[3], 10)), mo: MONTHS[m[1].toLowerCase().slice(0, 3)], d: parseInt(m[2], 10), rest: s.slice(m[0].length) };
    }
    return null;
};

// Returns { parts } or { error } — never silently drops.
const parseDT = (dateVal, timeVal) => {
    const dp = parseDatePart(dateVal);
    if (!dp) return { error: "Unrecognised date" };
    const t = timeVal !== undefined && timeVal !== "" ? parseTime(timeVal) : (dp.time || parseTime(dp.rest));
    if (!t) return { error: "Missing time" };
    const { y, mo, d } = dp;
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 2000 || y > 2100 || t.h > 23 || t.mi > 59) return { error: "Invalid date/time" };
    return { parts: { y, mo, d, h: t.h, mi: t.mi, serial: !!dp.serial } };
};

const buildDT = ({ y, mo, d, h, mi }) => ({
    dateKey: `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    ts: new Date(y, mo - 1, d, h, mi),
    tsMin: h * 60 + mi,
});

// "in by 10.40" / "10:40" / "1040" / "10" / "6.30 pm" → minutes since midnight.
const cueTime = (msg, cue, isOut) => {
    const m = msg.match(new RegExp(`${cue.source}\\s*(\\d{1,2})(?:[:.]?(\\d{2}))?(?!\\d)\\s*([ap])?\\.?\\s?(?:m\\b)?`, "i"));
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const mi = m[2] && parseInt(m[2], 10) < 60 ? parseInt(m[2], 10) : 0;
    const ap = (m[3] || "").toLowerCase();
    if (ap === "p" && h < 12) h += 12;
    else if (ap === "a" && h === 12) h = 0;
    else if (!ap && isOut && h <= 11) h += 12;
    if (h > 23) return null;
    return h * 60 + mi;
};

const pickFinal = (cands) => {
    if (!cands.length) return null;
    const actuals = cands.filter((c) => c.kind === "actual");
    const pool = actuals.length ? actuals : cands;
    return pool.reduce((a, b) => (b.ts > a.ts ? b : a));
};

const slabIn = (mins, shiftKey) => {
    if (mins === null || mins === undefined) return "No data";
    const bounds = slabsForShift(shiftKey).inBounds;
    // Use strict "<" for the first (Before) bucket so an exact shift-start
    // time lands in the first "in" bucket, not the "Before" one.
    if (mins < bounds[0].max) return bounds[0].label;
    for (let i = 1; i < bounds.length; i++) {
        if (mins <= bounds[i].max) return bounds[i].label;
    }
    return bounds[bounds.length - 1].label;
};
const slabOut = (mins, shiftKey) => {
    if (mins === null || mins === undefined) return "No data";
    const bounds = slabsForShift(shiftKey).outBounds;
    // Strict "<" on the first (Before) bucket so an exact shift-end time
    // is treated as on-time (first "out" bucket), not "early leave".
    if (mins < bounds[0].max) return bounds[0].label;
    for (let i = 1; i < bounds.length; i++) {
        if (mins <= bounds[i].max) return bounds[i].label;
    }
    return bounds[bounds.length - 1].label;
};

const classifyRecords = (recs, shiftKey = "corporate") => {
    const byEmp = {};
    recs.forEach((r) => {
        byEmp[r.employee] = byEmp[r.employee] || {};
        byEmp[r.employee][r.date] = byEmp[r.employee][r.date] || [];
        byEmp[r.employee][r.date].push(r);
    });
    const results = [];
    Object.keys(byEmp).forEach((emp) => {
        const dates = byEmp[emp];
        Object.keys(dates).sort().forEach((date) => {
            const msgs = dates[date];
            const isWfh = msgs.some((m) => /\bstarting\b/i.test(m.msg) || (/working remotely/i.test(m.msg) && !/tomorrow/i.test(m.msg)));
            const inCands = [], outCands = [];
            msgs.forEach((m) => {
                const msg = m.msg;
                if (/\b(?:oof|off) for lunch/i.test(msg)) return;
                if (isWfh) return;
                const ib = cueTime(msg, /\bin\s+(?:at|by)/i, false);
                if (ib != null) {
                    inCands.push({ mins: ib, msg, ts: m.ts, kind: "estimate" });
                } else if (/in now/i.test(msg)) {
                    inCands.push({ mins: m.tsMin, msg, ts: m.ts, kind: "actual" });
                } else if (/\+\d+\s*min/i.test(msg)) {
                    inCands.push({ mins: m.tsMin, msg, ts: m.ts, kind: "update" });
                }
                const oa = cueTime(msg, /\b(?:oof|off)\s+(?:at|by)/i, true);
                if (oa != null) {
                    outCands.push({ mins: oa, msg, ts: m.ts, kind: "estimate" });
                } else if (/\b(?:oof|off) now/i.test(msg)) {
                    outCands.push({ mins: m.tsMin, msg, ts: m.ts, kind: "actual" });
                }
            });
            const finalIn = pickFinal(inCands);
            const finalOut = pickFinal(outCands);
            results.push({
                employee: emp, date, isWfh,
                finalIn, finalOut,
                inSlab: isWfh ? "WFH" : slabIn(finalIn ? finalIn.mins : null, shiftKey),
                outSlab: isWfh ? "WFH" : slabOut(finalOut ? finalOut.mins : null, shiftKey),
                inCands, outCands,
            });
        });
    });
    return results;
};

const monthLabel = (m) => (m ? new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleString("en", { month: "short", year: "numeric" }) : "");

const findCol = (header, re, not) => header.findIndex((h) => re.test(h) && !(not && not.test(h)));

const parseWorkbook = async (file) => {
    const buf = await file.arrayBuffer();
    // raw for CSV stops SheetJS guessing dates as US M/D.
    const wb = XLSX.read(buf, { type: "array", cellDates: false, raw: /\.csv$/i.test(file.name) });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    const header = (rows[0] || []).map((h) => String(h).toLowerCase().trim());
    let dateIdx = findCol(header, /date|timestamp|^time$/);
    let timeIdx = findCol(header, /time/, /date|stamp/);
    let senderIdx = findCol(header, /sender|name|from|author|contact|user/);
    let msgIdx = findCol(header, /message|msg|text|body|content/);
    const hasHeader = dateIdx >= 0 && senderIdx >= 0 && msgIdx >= 0;
    if (!hasHeader) [dateIdx, senderIdx, msgIdx, timeIdx] = [0, 1, 2, -1];
    if (timeIdx === dateIdx) timeIdx = -1;

    const parsed = [];
    const skipped = [];
    let merged = 0;
    for (let r = hasHeader ? 1 : 0; r < rows.length; r++) {
        const row = rows[r] || [];
        const dtRaw = row[dateIdx];
        const sender = String(row[senderIdx] ?? "").trim();
        const msg = String(row[msgIdx] ?? "").trim();
        if (dtRaw === "" && sender === "" && msg === "") continue;
        if (dtRaw === "" && msg && parsed.length) { parsed[parsed.length - 1].msg += ` ${msg}`; merged += 1; continue; }
        const res = parseDT(dtRaw, timeIdx >= 0 ? row[timeIdx] : undefined);
        const reason = res.error || (!sender ? "Missing sender" : !msg ? "Empty message" : null);
        if (reason) { skipped.push({ file: file.name, row: r + 1, value: String(dtRaw), reason }); continue; }
        parsed.push({ parts: res.parts, employee: sender, msg });
    }
    // Mixed real-dates + text dates = Excel auto-converted only the M/D-valid cells → swap them back.
    const mixed = parsed.some((p) => p.parts.serial) && parsed.some((p) => !p.parts.serial);
    const recs = parsed.map(({ parts, employee, msg }) => {
        const fixed = mixed && parts.serial ? { ...parts, mo: parts.d, d: parts.mo } : parts;
        const { dateKey, ts, tsMin } = buildDT(fixed);
        return { employee, msg, date: dateKey, ts, tsMin };
    });
    return { recs, skipped, merged };
};

// ---- Robust name matching against Odyssey users ----------------------------
// Handles WhatsApp quirks: leading "~", trailing "(office)" / "[HR]" tags,
// phone numbers, first-name-only labels, and "First LastInitial" (e.g.
// "Sweekar M"). Falls back to Levenshtein distance ≤ 2 to catch typos.
const stripAliasNoise = (raw) => {
    if (!raw) return "";
    let t = String(raw).trim();
    if (t.startsWith("~")) t = t.slice(1).trim();
    // Drop trailing bracketed tags "(office)" / "[HR]" / etc.
    t = t.replace(/[[(].*?[\])]/g, "");
    // Phone-like sender → normalise to "phone::<last10digits>"
    const digits = t.replace(/\D/g, "");
    if (digits.length >= 8 && digits.length / Math.max(t.length, 1) > 0.6) {
        return { phone: digits.slice(-10), norm: `phone::${digits.slice(-10)}` };
    }
    return { norm: t.replace(/\s+/g, " ").trim().toLowerCase(), phone: null };
};

const levenshtein = (a, b) => {
    if (a === b) return 0;
    const m = a.length, n = b.length;
    if (Math.abs(m - n) > 3) return 99; // early exit — we only care about ≤ 2
    const dp = Array.from({ length: m + 1 }, (_, i) => i);
    for (let j = 1; j <= n; j++) {
        let prev = j - 1;
        dp[0] = j;
        for (let i = 1; i <= m; i++) {
            const cur = dp[i];
            dp[i] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[i - 1], dp[i]);
            prev = cur;
        }
    }
    return dp[m];
};

const matchOdysseyUser = (rawName, users, aliasMap) => {
    const norm = stripAliasNoise(rawName);
    const key = norm.norm || "";

    // 1. Alias table wins (explicit admin choice)
    if (aliasMap && aliasMap[key]) {
        const u = users.find((x) => x.user_id === aliasMap[key]);
        if (u) return { user: u, confidence: "alias" };
    }

    if (!key || key.startsWith("phone::")) {
        return { user: null, confidence: "unmatched" };
    }

    // 2. Exact full-name match
    const exact = users.find((u) => (u.name || "").trim().toLowerCase() === key);
    if (exact) return { user: exact, confidence: "exact" };

    // 3. "First LastInitial" form ("Sweekar M" / "Sweekar M.")
    const parts = key.replace(/\./g, "").split(/\s+/).filter(Boolean);
    if (parts.length === 2 && parts[1].length === 1) {
        const cand = users.filter((u) => {
            const p = (u.name || "").trim().toLowerCase().split(/\s+/);
            return p[0] === parts[0] && p[p.length - 1]?.startsWith(parts[1]);
        });
        if (cand.length === 1) return { user: cand[0], confidence: "first+initial" };
    }

    // 4. First-name only match — unique
    if (parts.length === 1) {
        const firstCand = users.filter((u) => (u.name || "").trim().toLowerCase().split(/\s+/)[0] === parts[0]);
        if (firstCand.length === 1) return { user: firstCand[0], confidence: "first-name" };
    }

    // 5. Last-name unique match
    if (parts.length >= 1) {
        const last = parts[parts.length - 1];
        const lastCand = users.filter((u) => {
            const p = (u.name || "").trim().toLowerCase().split(/\s+/);
            return p[p.length - 1] === last;
        });
        if (lastCand.length === 1) return { user: lastCand[0], confidence: "last-name" };
    }

    // 6. Levenshtein ≤ 2 against full name (typo tolerance)
    let bestDist = 99, best = null;
    for (const u of users) {
        const uname = (u.name || "").trim().toLowerCase();
        if (!uname) continue;
        const d = levenshtein(key, uname);
        if (d < bestDist) { bestDist = d; best = u; }
    }
    if (best && bestDist <= 2) return { user: best, confidence: "fuzzy" };

    return { user: null, confidence: "unmatched" };
};


const aggregateByEmployee = (results, shiftKey = "corporate") => {
    const shift = slabsForShift(shiftKey);
    const cols = [...shift.inSlabs, ...shift.outSlabs];
    const byEmp = {};
    for (const r of results) {
        if (!byEmp[r.employee]) {
            byEmp[r.employee] = { employee: r.employee, wfh_detected: 0, days: [], _shift: shiftKey };
            cols.forEach((s) => { byEmp[r.employee][s] = 0; });
        }
        if (r.isWfh) byEmp[r.employee].wfh_detected += 1;
        if (shift.inSlabs.includes(r.inSlab)) byEmp[r.employee][r.inSlab] += 1;
        if (shift.outSlabs.includes(r.outSlab)) byEmp[r.employee][r.outSlab] += 1;
        byEmp[r.employee].days.push(r);
    }
    return Object.values(byEmp).sort((a, b) => a.employee.localeCompare(b.employee));
};

// --- Message drill-down modal ---
const MessageDrilldownModal = ({ payload, onClose }) => {
    if (!payload) return null;
    const { employee, slab, days, finalKind } = payload;
    return createPortal(
        <div className="fixed inset-0 z-[220] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Attribution · {employee}</div>
                        <div className="font-heading font-semibold text-slate-900 text-sm">{slab} · {days.length} day{days.length === 1 ? "" : "s"}</div>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 inline-flex items-center justify-center">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-4 overflow-y-auto flex-1 space-y-3">
                    {days.map((d, i) => {
                        const cands = finalKind === "in" ? d.inCands : d.outCands;
                        const final = finalKind === "in" ? d.finalIn : d.finalOut;
                        return (
                            <div key={i} className="rounded-lg border border-slate-200 overflow-hidden">
                                <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-700 border-b border-slate-100">
                                    {d.date} · {cands.length} candidate message{cands.length === 1 ? "" : "s"}
                                </div>
                                <ul className="divide-y divide-slate-100">
                                    {cands.map((c, j) => {
                                        const isFinal = final && c.ts === final.ts && c.msg === final.msg;
                                        return (
                                            <li key={j} className={`px-3 py-2 text-xs ${isFinal ? "bg-emerald-50/60" : ""}`}>
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="text-slate-500 tabular-nums">{new Date(c.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
                                                    <span className={`px-1.5 h-4 rounded-full text-[9px] font-bold uppercase tracking-wider ${c.kind === "actual" ? "bg-[#2EB4AC]/10 text-[#25908A]" : c.kind === "estimate" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                                                        {c.kind}
                                                    </span>
                                                    {c.kind === "estimate" && Number.isFinite(c.mins) && (
                                                        <span className="text-[10px] font-semibold text-slate-600 tabular-nums" data-testid="drill-counted-time">→ counted as {String(Math.floor(c.mins / 60)).padStart(2, "0")}:{String(c.mins % 60).padStart(2, "0")}</span>
                                                    )}
                                                    {isFinal && <span className="text-[10px] font-bold text-emerald-700">↳ chosen</span>}
                                                </div>
                                                <div className="text-slate-700 leading-snug">{c.msg}</div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body,
    );
};

// --- Main component ---
const AttendanceChatClassifier = ({ visibleUsers, onPublished }) => {
    const [files, setFiles] = useState([]); // {name, file}
    const [dragOver, setDragOver] = useState(false);
    const [results, setResults] = useState(null);
    const [processing, setProcessing] = useState(false);
    // Shift selector — decides which slab bounds classifyRecords uses AND
    // which slab column headers show up in the Classified box. Persists
    // per user in the row's `shift` field on chat-publish.
    const [shiftKey, setShiftKey] = useState("corporate");
    const activeShift = slabsForShift(shiftKey);
    const activeSlabCols = allSlabCols(shiftKey);
    // Months found in the upload; each publishes to its own attendance record.
    const [viewMonth, setViewMonth] = useState("");
    const [woByMonth, setWoByMonth] = useState({}); // { [YYYY-MM]: { [emp]: { wfh, oof } } }
    const [parseReport, setParseReport] = useState(null); // { parsed, skipped: [], merged }
    // Per-employee WFH/OOF overrides. Key: employee name (as found in chat).
    const [wfhOof, setWfhOof] = useState({}); // { [emp]: { wfh:0, oof:0, user_id, department } }
    // Per-row toggle: when the fuzzy match yields something but the user
    // wants to override with any user, they click "all" to expand the
    // dropdown from filtered matches to the whole directory.
    const [showAllForRow, setShowAllForRow] = useState({});
    // Which mapped rows are currently open for remapping (inline dropdown in the Classified table).
    const [remapOpen, setRemapOpen] = useState({});
    // Toggle "show all users" per row inside the inline remap dropdown.
    const [remapShowAll, setRemapShowAll] = useState({});
    const [publishing, setPublishing] = useState(false);
    const [drill, setDrill] = useState(null); // {employee, slab, days, finalKind}
    // Curated chat-sender → user_id map, hydrated from backend on mount.
    const [aliasMap, setAliasMap] = useState({});
    const fileInputRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/attendance/name-aliases");
                const m = {};
                for (const a of r.data?.aliases || []) if (a.alias_key && a.user_id) m[a.alias_key] = a.user_id;
                setAliasMap(m);
            } catch { /* silent — aliases are optional */ }
        })();
    }, []);

    const addFiles = (list) => {
        const next = [...files];
        for (const f of list) {
            if (!next.find((x) => x.name === f.name)) next.push({ name: f.name, file: f });
        }
        setFiles(next);
    };
    const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));
    const clearAll = () => { setFiles([]); setResults(null); setWfhOof({}); setWoByMonth({}); setParseReport(null); };

    const process = async () => {
        setProcessing(true);
        try {
            const allRecs = [];
            const skipped = [];
            let merged = 0;
            for (const f of files) {
                const out = await parseWorkbook(f.file);
                allRecs.push(...out.recs);
                skipped.push(...out.skipped);
                merged += out.merged;
            }
            setParseReport({ parsed: allRecs.length, skipped, merged });
            if (allRecs.length === 0) {
                setResults(null);
                toast.error(`No readable rows found${skipped.length ? ` · ${skipped.length} rows skipped` : ""}`);
                return;
            }
            const classified = classifyRecords(allRecs, shiftKey);
            const agg = aggregateByEmployee(classified, shiftKey);
            const months = [...new Set(classified.map((c) => c.date.slice(0, 7)))].sort();
            const byMonth = Object.fromEntries(months.map((m) => [m, aggregateByEmployee(classified.filter((c) => c.date.startsWith(m)), shiftKey)]));
            setResults({ classified, agg, months, byMonth });
            setViewMonth(months[months.length - 1]);
            setWoByMonth(Object.fromEntries(months.map((m) => [m, Object.fromEntries(byMonth[m].map((r) => [r.employee, { wfh: r.wfh_detected || 0, oof: 0 }]))])));
            // Seed WFH/OOF defaults + resolve names via robust matcher.
            const seeds = {};
            agg.forEach((row) => {
                const m = matchOdysseyUser(row.employee, visibleUsers || [], aliasMap);
                seeds[row.employee] = {
                    user_id: m.user?.user_id || "",
                    department: m.user?.department_name || "",
                    matched_name: m.user?.name || "",
                    confidence: m.confidence,
                };
            });
            setWfhOof(seeds);
            toast.success(`Classified ${classified.length} day-employee records from ${agg.length} people across ${months.length} month${months.length === 1 ? "" : "s"}`);
        } catch (e) {
            toast.error("Parse failed: " + (e.message || "unknown"));
        } finally {
            setProcessing(false);
        }
    };

    const uploadWfhOofXlsx = async (file) => {
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
            let n = 0;
            const next = { ...(woByMonth[viewMonth] || {}) };
            for (const r of rows) {
                const empKey = Object.keys(r).find((k) => /employee|name|first ?name|sender/i.test(k));
                const wfhKey = Object.keys(r).find((k) => /wfh|work from home/i.test(k));
                const oofKey = Object.keys(r).find((k) => /oof|leaves|off/i.test(k));
                if (!empKey) continue;
                const emp = String(r[empKey]).trim();
                if (!emp || !next[emp]) continue;
                next[emp] = {
                    ...next[emp],
                    ...(wfhKey ? { wfh: Number(r[wfhKey]) || 0 } : {}),
                    ...(oofKey ? { oof: Number(r[oofKey]) || 0 } : {}),
                };
                n += 1;
            }
            setWoByMonth({ ...woByMonth, [viewMonth]: next });
            toast.success(`Applied WFH/OOF for ${n} employees · ${monthLabel(viewMonth)}`);
        } catch (e) {
            toast.error("Failed to read WFH/OOF file");
        }
    };

    const publish = async () => {
        if (!results) return;
        // Skip any row whose sender wasn't mapped to an Odyssey user.
        // These records would land as unattributed rows and pollute the
        // attendance grid, so we drop them here and warn the caller.
        const publishable = results.agg.filter((r) => (wfhOof[r.employee] || {}).user_id);
        const skipped = results.agg.length - publishable.length;
        if (publishable.length === 0) {
            toast.error("No mapped employees to publish — map at least one sender first");
            return;
        }
        if (skipped > 0) {
            const proceed = window.confirm(
                `${skipped} unmapped sender${skipped === 1 ? "" : "s"} will be skipped. Publish the remaining ${publishable.length} row${publishable.length === 1 ? "" : "s"}?`
            );
            if (!proceed) return;
        }
        setPublishing(true);
        try {
            const total = { upserted: 0, messages_logged: 0, kno_synced: 0, kno_skipped_manual: 0, kno_skipped_cancelled: 0 };
            for (const m of results.months) {
                const rows = results.byMonth[m].filter((r) => (wfhOof[r.employee] || {}).user_id).map((r) => {
                    const w = { ...(wfhOof[r.employee] || {}), ...((woByMonth[m] || {})[r.employee] || {}) };
                    const sh = slabsForShift(shiftKey);
                    // Store slab counts under generic slot-1/2/3 keys keyed by
                    // the row's shift so the attendance grid can adapt the
                    // label per row without a schema explosion.
                    return {
                        employee_name: r.employee,
                        user_id: w.user_id,
                        department_name: w.department || undefined,
                        shift: shiftKey,
                        late_slab_1: r[sh.inSlabs[0]] || 0,
                        late_slab_2: r[sh.inSlabs[1]] || 0,
                        late_slab_3: r[sh.inSlabs[2]] || 0,
                        early_slab_1: r[sh.outSlabs[0]] || 0,
                        early_slab_2: r[sh.outSlabs[1]] || 0,
                        early_slab_3: r[sh.outSlabs[2]] || 0,
                        wfh: Number(w.wfh) || 0,
                        oof: Number(w.oof) || 0,
                        messages: r.days.map((d) => ({
                            date: d.date,
                            isWfh: !!d.isWfh,
                            inSlab: d.inSlab,
                            outSlab: d.outSlab,
                            finalIn: d.finalIn ? { mins: d.finalIn.mins, msg: d.finalIn.msg, kind: d.finalIn.kind, ts: new Date(d.finalIn.ts).toISOString() } : null,
                            finalOut: d.finalOut ? { mins: d.finalOut.mins, msg: d.finalOut.msg, kind: d.finalOut.kind, ts: new Date(d.finalOut.ts).toISOString() } : null,
                            inCands: d.inCands.map((c) => ({ mins: c.mins, msg: c.msg, kind: c.kind, ts: new Date(c.ts).toISOString() })),
                            outCands: d.outCands.map((c) => ({ mins: c.mins, msg: c.msg, kind: c.kind, ts: new Date(c.ts).toISOString() })),
                        })),
                    };
                });
                if (!rows.length) continue;
                const res = await api.post("/attendance/chat-publish", { month: m, rows });
                Object.keys(total).forEach((k) => { total[k] += res.data?.[k] || 0; });
            }
            const detail = skipped > 0 ? ` · ${skipped} unmapped skipped` : "";
            const monthsTxt = results.months.length > 1 ? ` across ${results.months.length} months` : ` to ${monthLabel(results.months[0])}`;
            toast.success(`Published${monthsTxt}: ${total.upserted} rows saved · ${total.messages_logged} messages archived${detail}`);
            const knoSynced = total.kno_synced;
            const knoManual = total.kno_skipped_manual;
            const knoCancelled = total.kno_skipped_cancelled;
            if (knoSynced || knoManual || knoCancelled) {
                const parts = [];
                if (knoSynced) parts.push(`${knoSynced} synced`);
                if (knoManual) parts.push(`${knoManual} manual kept`);
                if (knoCancelled) parts.push(`${knoCancelled} cancelled kept`);
                toast(`KNO auto-updated · ${parts.join(" · ")}`, { icon: "🔄" });
            }
            if (onPublished) onPublished();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Publish failed");
        } finally {
            setPublishing(false);
        }
    };

    const openDrill = (row, slab) => {
        const finalKind = activeShift.inSlabs.includes(slab) ? "in" : "out";
        const days = row.days.filter((d) => (finalKind === "in" ? d.inSlab === slab : d.outSlab === slab));
        setDrill({ employee: row.employee, slab, days, finalKind });
    };

    const setManualMap = async (chatName, userId) => {
        const u = (visibleUsers || []).find((x) => x.user_id === userId);
        setWfhOof((prev) => ({
            ...prev,
            [chatName]: {
                ...(prev[chatName] || { wfh: 0, oof: 0 }),
                user_id: userId,
                department: u?.department_name || "",
                matched_name: u?.name || "",
                confidence: "manual",
            },
        }));
        if (!userId) return;
        // Persist alias so future imports auto-resolve — silent on failure
        try {
            await api.post("/attendance/name-aliases", { alias: chatName, user_id: userId });
            setAliasMap((m) => ({ ...m, [chatName.toLowerCase().trim()]: userId }));
            toast.success(`Saved mapping — future imports will auto-match "${chatName}"`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save alias");
        }
    };

    const unmatchedRows = useMemo(
        () => (results?.agg || []).filter((r) => !(wfhOof[r.employee] || {}).user_id),
        [results, wfhOof],
    );
    // Aggregate view (the "Classified" box) only shows mapped employees
    // so unmapped chat senders don't clutter the summary + accidentally
    // get expected to land in attendance. Backing count kept for the
    // "N unmapped hidden" hint.
    const monthAgg = results?.byMonth?.[viewMonth] || [];
    const mappedAgg = useMemo(
        () => monthAgg.filter((r) => (wfhOof[r.employee] || {}).user_id),
        [monthAgg, wfhOof],
    );
    const hiddenUnmappedCount = monthAgg.length - mappedAgg.length;
    const publishCount = (results?.months || []).reduce((n, m) => n + results.byMonth[m].filter((r) => (wfhOof[r.employee] || {}).user_id).length, 0);
    const setWO = (emp, field, val) =>
        setWoByMonth((p) => ({ ...p, [viewMonth]: { ...(p[viewMonth] || {}), [emp]: { ...((p[viewMonth] || {})[emp] || { wfh: 0, oof: 0 }), [field]: val } } }));


    // --- Fuzzy-match helper: only surface Odyssey users whose name has a
    // plausible overlap with the chat sender's name. Prevents the dropdown
    // from becoming a 400-name scroll when the user just needs one obvious
    // match. Uses token overlap (case + accent insensitive) + a prefix check.
    const _normName = (s) => (s || "").toLowerCase().normalize("NFKD").replace(/[^a-z\s]/g, " ").split(/\s+/).filter(Boolean);
    const usersForChatName = (chatName) => {
        const chatTokens = new Set(_normName(chatName));
        if (chatTokens.size === 0) return visibleUsers || [];
        const scored = [];
        for (const u of visibleUsers || []) {
            const nameTokens = _normName(u.name);
            let score = 0;
            for (const t of nameTokens) {
                if (t.length < 2) continue;
                if (chatTokens.has(t)) score += 3;                       // exact token match
                else {
                    for (const c of chatTokens) {
                        if (c.length < 2) continue;
                        if (c.startsWith(t) || t.startsWith(c)) { score += 1; break; }
                    }
                }
            }
            if (score > 0) scored.push({ u, score });
        }
        scored.sort((a, b) => b.score - a.score || (a.u.name || "").localeCompare(b.u.name || ""));
        return scored.map((x) => x.u);
    };

    const totalForRow = (r) => activeSlabCols.reduce((s, k) => s + (r[k] || 0), 0);

    return (
        <div className="space-y-5" data-testid="chat-classifier">
            <div className="rounded-xl border-2 border-dashed border-fuchsia-300/60 bg-gradient-to-br from-fuchsia-50/40 via-white to-white p-4">
                <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm">
                        BETA
                    </span>
                    <div className="font-heading font-bold text-slate-900 text-sm">Attendance from chat exports</div>
                </div>
                <p className="text-xs text-slate-500">
                    Drop one WhatsApp export per employee (or a single mixed-sender export). We parse each day&rsquo;s <code className="bg-slate-100 px-1 rounded">in by</code>/<code className="bg-slate-100 px-1 rounded">in now</code>/<code className="bg-slate-100 px-1 rounded">oof at</code>/<code className="bg-slate-100 px-1 rounded">off by</code>/<code className="bg-slate-100 px-1 rounded">oof now</code> cues (12h or 24h times like 1800), bucket into slabs, and let you drill into the exact messages that decided each count. WFH/OOF are filled in below (inline or via a small XLSX).
                </p>
            </div>

            {/* Shift selector — decides which slab boundaries + column headers we use. */}
            <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3 flex-wrap">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold shrink-0">Shift</div>
                <div className="inline-flex rounded-lg bg-slate-100 p-0.5 gap-0.5" data-testid="chat-shift-selector">
                    {Object.values(SHIFTS).map((s) => (
                        <button
                            key={s.key}
                            type="button"
                            onClick={() => {
                                setShiftKey(s.key);
                                // Bin any stale classification — the slab
                                // headers now don't match the aggregate.
                                // Forcing a re-run keeps the UI honest.
                                if (results) {
                                    setResults(null);
                                    toast.info("Shift changed — click Classify again to re-bucket the messages.");
                                }
                            }}
                            data-testid={`chat-shift-${s.key}`}
                            className={`h-8 px-3 rounded-md text-xs font-semibold transition ${
                                shiftKey === s.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            {s.key === "corporate" ? "Corporate Shift" : "Firmware Shift"}
                        </button>
                    ))}
                </div>
                <span className="text-[11px] text-slate-500">{activeShift.label} · counts under these slabs will publish to attendance</span>
            </div>

            {/* Dropzone */}
            <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                data-testid="chat-classifier-dropzone"
                className={`rounded-xl border-2 border-dashed transition-colors cursor-pointer p-6 text-center ${dragOver ? "border-[#2EB4AC] bg-[#2EB4AC]/5" : "border-slate-300 hover:border-slate-400 bg-slate-50/60"}`}
            >
                <UploadCloud className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                <div className="text-sm font-semibold text-slate-700">Drop chat exports here, or click to choose</div>
                <div className="text-xs text-slate-500 mt-1">.xlsx or .csv · columns: DateTime (or Date + Time), Sender, Message · any common date format</div>
                <input ref={fileInputRef} type="file" multiple accept=".xlsx,.csv,.xls" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </div>

            {files.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {files.map((f, i) => (
                        <div key={f.name} className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-white border border-slate-200 text-xs">
                            <FileSpreadsheet className="h-3.5 w-3.5 text-[#25908A]" />
                            <span className="font-medium text-slate-700 truncate max-w-[220px]">{f.name}</span>
                            <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-rose-600" aria-label="Remove">
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={process}
                    disabled={files.length === 0 || processing}
                    data-testid="chat-classifier-process"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold disabled:opacity-50"
                >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                    {processing ? "Classifying…" : `Classify ${files.length || ""} file${files.length === 1 ? "" : "s"}`}
                </button>
                {(files.length > 0 || results) && (
                    <button type="button" onClick={clearAll} className="h-10 px-3 rounded-lg text-slate-500 hover:bg-slate-100 text-sm font-medium">
                        Clear
                    </button>
                )}
            </div>

            {parseReport && (parseReport.skipped.length > 0 || parseReport.merged > 0) && (
                <div className={`rounded-xl border p-4 ${parseReport.skipped.length ? "border-amber-300 bg-amber-50/70" : "border-slate-200 bg-slate-50"}`} data-testid="chat-parse-report">
                    <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">
                        {parseReport.skipped.length > 0 && <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                        Read {parseReport.parsed} message{parseReport.parsed === 1 ? "" : "s"}
                        {parseReport.skipped.length > 0 && <span className="text-amber-800" data-testid="chat-skipped-count"> · {parseReport.skipped.length} row{parseReport.skipped.length === 1 ? "" : "s"} skipped</span>}
                        {parseReport.merged > 0 && <span className="text-slate-500"> · {parseReport.merged} multi-line continuation{parseReport.merged === 1 ? "" : "s"} joined to the message above</span>}
                    </div>
                    {parseReport.skipped.length > 0 && (
                        <ul className="mt-2 space-y-1 text-[11px] text-slate-600" data-testid="chat-skipped-examples">
                            <Expandable
                                items={parseReport.skipped}
                                limit={5}
                                className="text-[#25908A] font-semibold"
                                testid="chat-skipped-more"
                                render={(x, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="text-slate-400 tabular-nums shrink-0">{x.file} · row {x.row}</span>
                                        <span className="font-mono text-slate-700 truncate">{x.value || "(empty)"}</span>
                                        <span className="text-amber-700 shrink-0">{x.reason}</span>
                                    </li>
                                )}
                            />
                        </ul>
                    )}
                </div>
            )}

            {results && (
                <>
                    {unmatchedRows.length > 0 && (
                        <div
                            data-testid="chat-classifier-unmapped-strip"
                            className="rounded-xl border-2 border-amber-300 bg-amber-50/70 p-4"
                        >
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div>
                                    <div className="text-[10px] font-black uppercase tracking-wider text-amber-800 inline-flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        {unmatchedRows.length} sender{unmatchedRows.length === 1 ? "" : "s"} don&rsquo;t map to Odyssey users
                                    </div>
                                    <div className="text-xs text-amber-800/80 mt-1">
                                        Pick the right Odyssey person — we&rsquo;ll remember this mapping for future imports.
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                {unmatchedRows.map((r) => {
                                    const matches = usersForChatName(r.employee);
                                    const showAll = !!showAllForRow[r.employee];
                                    const options = (matches.length === 0 || showAll)
                                        ? (visibleUsers || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                        : matches;
                                    return (
                                        <div key={r.employee} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-amber-200">
                                            <span className="text-sm font-semibold text-slate-800 flex-1 truncate" title={r.employee}>{r.employee}</span>
                                            <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />
                                            <select
                                                value={(wfhOof[r.employee] || {}).user_id || ""}
                                                onChange={(e) => setManualMap(r.employee, e.target.value)}
                                                data-testid={`chat-map-${r.employee}`}
                                                className="h-8 px-2 rounded-md border border-slate-200 bg-white text-xs focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#2EB4AC]/30 max-w-[280px]"
                                            >
                                                <option value="">
                                                    {matches.length === 0
                                                        ? "— no name match found —"
                                                        : `— pick from ${options.length} match${options.length === 1 ? "" : "es"} —`}
                                                </option>
                                                {options.map((u) => (
                                                    <option key={u.user_id} value={u.user_id}>
                                                        {u.name}{u.department_name ? ` · ${u.department_name}` : ""}
                                                    </option>
                                                ))}
                                            </select>
                                            {matches.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAllForRow((p) => ({ ...p, [r.employee]: !showAll }))}
                                                    data-testid={`chat-map-showall-${r.employee}`}
                                                    className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-[#25908A]"
                                                    title={showAll ? "Show only name matches" : "Show every Odyssey user"}
                                                >
                                                    {showAll ? "matches" : "all"}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                Classified · {mappedAgg.length} mapped employee{mappedAgg.length === 1 ? "" : "s"}
                                {hiddenUnmappedCount > 0 && (
                                    <span className="ml-2 normal-case text-slate-400 tracking-normal">
                                        · {hiddenUnmappedCount} unmapped hidden
                                    </span>
                                )}
                                <span className="ml-2 normal-case text-slate-400 tracking-normal">
                                    · {results.classified.filter((c) => c.date.startsWith(viewMonth)).length} day-records
                                </span>
                            </div>
                            {results.months.length > 1 && (
                                <div className="inline-flex rounded-lg bg-white border border-slate-200 p-0.5 gap-0.5" data-testid="chat-month-tabs">
                                    {results.months.map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => setViewMonth(m)}
                                            data-testid={`chat-month-tab-${m}`}
                                            className={`h-7 px-2.5 rounded-md text-[11px] font-semibold transition-colors ${viewMonth === m ? "bg-[#2EB4AC] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                                        >
                                            {monthLabel(m)}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="text-[11px] text-slate-500">Click any count to see the exact messages that produced it.</div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-white border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                        <th className="text-left px-3 py-2">Employee</th>
                                        {activeSlabCols.map((s) => <th key={s} className="text-right px-2 py-2 whitespace-nowrap">{s}</th>)}
                                        <th className="text-right px-2 py-2">WFH</th>
                                        <th className="text-right px-2 py-2">OOF</th>
                                        <th className="text-right px-3 py-2">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {mappedAgg.map((r) => {
                                        const w = wfhOof[r.employee] || {};
                                        const wo = (woByMonth[viewMonth] || {})[r.employee] || { wfh: 0, oof: 0 };
                                        return (
                                            <tr key={r.employee} className="hover:bg-slate-50/60">
                                                <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">
                                                    {remapOpen[r.employee] ? (() => {
                                                        const matches = usersForChatName(r.employee);
                                                        const showAll = !!remapShowAll[r.employee];
                                                        const options = (matches.length === 0 || showAll)
                                                            ? (visibleUsers || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                                            : matches;
                                                        return (
                                                            <div className="flex items-center gap-1.5 max-w-[380px]" data-testid={`remap-row-${r.employee}`}>
                                                                <span className="text-slate-600 truncate max-w-[110px]" title={r.employee}>{r.employee}</span>
                                                                <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />
                                                                <select
                                                                    autoFocus
                                                                    value={w.user_id || ""}
                                                                    onChange={async (e) => {
                                                                        await setManualMap(r.employee, e.target.value);
                                                                        setRemapOpen((p) => ({ ...p, [r.employee]: false }));
                                                                    }}
                                                                    data-testid={`remap-select-${r.employee}`}
                                                                    className="h-7 px-1.5 rounded-md border border-slate-200 bg-white text-xs focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#2EB4AC]/30 max-w-[220px]"
                                                                >
                                                                    <option value="">— unmap this row —</option>
                                                                    {options.map((u) => (
                                                                        <option key={u.user_id} value={u.user_id}>
                                                                            {u.name}{u.department_name ? ` · ${u.department_name}` : ""}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {matches.length > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setRemapShowAll((p) => ({ ...p, [r.employee]: !showAll }))}
                                                                        data-testid={`remap-showall-${r.employee}`}
                                                                        className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-[#25908A]"
                                                                        title={showAll ? "Show only name matches" : "Show every Odyssey user"}
                                                                    >
                                                                        {showAll ? "matches" : "all"}
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setRemapOpen((p) => ({ ...p, [r.employee]: false }))}
                                                                    className="text-slate-400 hover:text-rose-600"
                                                                    aria-label="Cancel remap"
                                                                    data-testid={`remap-cancel-${r.employee}`}
                                                                >
                                                                    <X className="h-3.5 w-3.5" />
                                                                </button>
                                                            </div>
                                                        );
                                                    })() : (
                                                        <div className="flex items-center gap-1.5 group">
                                                            <span>{r.employee}</span>
                                                            {w.matched_name && w.matched_name.toLowerCase() !== r.employee.toLowerCase() && (
                                                                <span
                                                                    className="text-[9px] font-semibold uppercase tracking-wider px-1.5 h-4 rounded-full inline-flex items-center gap-0.5 bg-[#2EB4AC]/10 text-[#25908A] ring-1 ring-[#2EB4AC]/20"
                                                                    title={`Auto-matched (${w.confidence}) to ${w.matched_name}`}
                                                                >
                                                                    → {w.matched_name}
                                                                </span>
                                                            )}
                                                            {!w.user_id && (
                                                                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 inline-flex items-center gap-0.5" title="No Odyssey user matched — pick one in the strip above">
                                                                    <AlertTriangle className="h-3 w-3" />
                                                                    Unmapped
                                                                </span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => setRemapOpen((p) => ({ ...p, [r.employee]: true }))}
                                                                data-testid={`remap-open-${r.employee}`}
                                                                className="ml-1 inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-[#25908A] hover:bg-[#2EB4AC]/10 opacity-70 group-hover:opacity-100 transition"
                                                                title="Change the Odyssey person this sender maps to"
                                                            >
                                                                <Pencil className="h-3 w-3" />
                                                                Change
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                                {activeSlabCols.map((s) => (
                                                    <td key={s} className="text-right px-2 py-2 tabular-nums">
                                                        <button
                                                            type="button"
                                                            onClick={() => openDrill(r, s)}
                                                            disabled={!r[s]}
                                                            data-testid={`slab-cell-${r.employee}-${s}`}
                                                            className={`px-1.5 rounded ${r[s] ? "text-[#25908A] font-semibold hover:bg-[#2EB4AC]/10" : "text-slate-300"}`}
                                                        >
                                                            {r[s] || "—"}
                                                        </button>
                                                    </td>
                                                ))}
                                                <td className="text-right px-2 py-2">
                                                    <input
                                                        type="number" min="0" step="0.5"
                                                        value={wo.wfh}
                                                        data-testid={`chat-wfh-${r.employee}`}
                                                        onChange={(e) => setWO(r.employee, "wfh", e.target.value)}
                                                        className="w-14 h-7 px-1.5 rounded border border-slate-200 text-right text-xs focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#2EB4AC]/30"
                                                    />
                                                </td>
                                                <td className="text-right px-2 py-2">
                                                    <input
                                                        type="number" min="0" step="0.5"
                                                        value={wo.oof}
                                                        data-testid={`chat-oof-${r.employee}`}
                                                        onChange={(e) => setWO(r.employee, "oof", e.target.value)}
                                                        className="w-14 h-7 px-1.5 rounded border border-slate-200 text-right text-xs focus:border-[#2EB4AC] focus:ring-1 focus:ring-[#2EB4AC]/30"
                                                    />
                                                </td>
                                                <td className="text-right px-3 py-2 tabular-nums font-bold text-slate-900">
                                                    {totalForRow(r)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Publish bar */}
                    <div className="rounded-xl bg-slate-900 text-white p-4 flex flex-wrap items-center gap-3 justify-between">
                        <div className="flex items-center gap-3">
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-white/60">Publish to attendance</div>
                                <div className="text-sm text-white/90">Upserts <span className="font-semibold">{publishCount}</span> mapped row{publishCount === 1 ? "" : "s"}{results.months.length > 1 ? ` across ${results.months.length} months (one record per person per month)` : ""} and archives the message trail for cell-level drill-down.{hiddenUnmappedCount > 0 && (<span className="text-white/60"> · {hiddenUnmappedCount} unmapped will be skipped.</span>)}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <label className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">{results.months.length > 1 ? "Months" : "Month"}</label>
                            <div className="flex flex-wrap gap-1" data-testid="chat-classifier-months">
                                {results.months.map((m) => (
                                    <span key={m} className="inline-flex items-center h-7 px-2 rounded-md bg-white/10 border border-white/20 text-xs font-semibold">{monthLabel(m)}</span>
                                ))}
                            </div>
                            <label className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-white/10 border border-white/20 text-white text-xs font-semibold cursor-pointer hover:bg-white/20">
                                <UploadCloud className="h-3.5 w-3.5" />
                                WFH/OOF XLSX
                                <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadWfhOofXlsx(e.target.files[0]); e.target.value = ""; }} />
                            </label>
                            <button
                                type="button"
                                onClick={publish}
                                disabled={publishing}
                                data-testid="chat-classifier-publish"
                                className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold disabled:opacity-60"
                            >
                                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                {publishing ? "Publishing…" : "Publish to attendance"}
                            </button>
                        </div>
                    </div>
                </>
            )}

            <MessageDrilldownModal payload={drill} onClose={() => setDrill(null)} />
        </div>
    );
};

export default AttendanceChatClassifier;
